// Client wrapper for invite-code onboarding (multi-facility provisioning).
// createInvite/redeemInvite go through Cloud Functions (Admin SDK sets the
// user's companyId/role server-side — the client can't, by the C1 rule).
// list/revoke are same-company admin Firestore ops (see the `invites` rules).

import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { UserRole } from '../models/User';

export interface Invite {
  code: string;
  companyId: string;
  role: UserRole;
  createdBy: string;
  createdByName?: string;
  createdAt?: { toMillis?: () => number };
  expiresAt?: { toMillis?: () => number };
  active: boolean;
  uses: number;
  maxUses?: number;
}

export interface CreatedInvite {
  code: string;
  companyId: string;
  expiresAt: string;
}

class InviteService {
  /** Admin: mint an invite code for this facility + a role. */
  async createInvite(role: UserRole, expiresInDays?: number, maxUses?: number): Promise<CreatedInvite> {
    const fn = httpsCallable(getFunctions(), 'createInvite');
    const res = (await fn({ role, expiresInDays, maxUses })) as {
      data: { success: boolean; code: string; companyId: string; expiresAt: string };
    };
    return { code: res.data.code, companyId: res.data.companyId, expiresAt: res.data.expiresAt };
  }

  /** New user: redeem a code to join its facility (server sets companyId + role). */
  async redeemInvite(code: string): Promise<{ companyId: string; role: UserRole }> {
    const fn = httpsCallable(getFunctions(), 'redeemInvite');
    const res = (await fn({ code: code.trim().toUpperCase() })) as {
      data: { success: boolean; companyId: string; role: UserRole };
    };
    return { companyId: res.data.companyId, role: res.data.role };
  }

  /** Admin: list this facility's invites (newest first; client-sorted, no index). */
  async listInvites(companyId: string): Promise<Invite[]> {
    const q = query(collection(db, 'invites'), where('companyId', '==', companyId));
    const snap = await getDocs(q);
    const invites = snap.docs.map((d) => ({ code: d.id, ...(d.data() as Omit<Invite, 'code'>) }));
    return invites.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
  }

  /** Admin: revoke an invite (soft — sets active:false). */
  async revokeInvite(code: string): Promise<void> {
    await updateDoc(doc(db, 'invites', code), { active: false });
  }
}

export const inviteService = new InviteService();
